import { skipToken } from '@reduxjs/toolkit/query';
import { Button } from '@strata/react-buttons';
import { DialogWithForm } from '@strata/react-dialog';
import { TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useState } from 'react';
import * as Yup from 'yup';

import { snoozeAvailableAt, SnoozePreset } from 'services/snoozeRequest';
import {
    useGetRequestForConversationQuery,
    useSnoozeRequestMutation,
} from 'store/api/agoraMonitorApi';

const PRESET_OPTIONS = [
    { value: SnoozePreset.tomorrowMorning, text: 'Tomorrow 9:00 AM ET' },
    { value: SnoozePreset.plus24Hours, text: 'In 24 hours' },
    { value: SnoozePreset.custom, text: 'Pick a time' },
];

const validationSchema = Yup.object({
    preset: Yup.string().required(validationStrings.requiredField.errorMessage),
    note: Yup.string().max(500, validationStrings.maxCharacter[500]),
    customAt: Yup.string().when('preset', {
        is: SnoozePreset.custom,
        then: (schema) =>
            schema
                .required(validationStrings.requiredField.errorMessage)
                .test('future', 'Pick a time in the future', (value) => {
                    const at = snoozeAvailableAt(SnoozePreset.custom, value);
                    return at != null && dayjs(at).isAfter(dayjs());
                }),
    }),
});

export const SnoozeRequestDialog = ({ isOpen, requestId, onClose }) => {
    const [snoozeRequest] = useSnoozeRequestMutation();

    return (
        <Formik
            initialValues={{ preset: SnoozePreset.tomorrowMorning, note: '', customAt: '' }}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) => {
                const availableAtUtc = snoozeAvailableAt(values.preset, values.customAt);
                await snoozeRequest({
                    requestId,
                    availableAtUtc,
                    label: values.note.trim(),
                }).unwrap();
                SuccessToast({ message: 'Request snoozed.' });
                resetForm();
                onClose();
            }}
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={isOpen}
                    dialogTitle="Snooze"
                    width={440}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        formik.resetForm();
                        onClose();
                    }}
                    primaryButtonLabel="Snooze"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <RadioButtonGroupFormik
                                name="preset"
                                label="Check in"
                                options={PRESET_OPTIONS}
                            />
                            {formik.values.preset === SnoozePreset.custom ? (
                                <TextInputFormik
                                    id="customAt"
                                    label="Check-in time"
                                    type="datetime-local"
                                />
                            ) : null}
                            <TextInputFormik id="note" label="Note" />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

SnoozeRequestDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    requestId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};

export const SnoozeRequestControl = ({ conversationId = undefined }) => {
    const { showWorkflowManagementLayer = false } = useFlags();
    const [snoozeOpen, setSnoozeOpen] = useState(false);
    const { data } = useGetRequestForConversationQuery(
        showWorkflowManagementLayer && conversationId ? conversationId : skipToken,
    );
    if (!showWorkflowManagementLayer || !data?.requestId) {
        return null;
    }
    return (
        <div className="flex__gap--small margin__bottom--small">
            <Button label="Snooze" onClick={() => setSnoozeOpen(true)} />
            <SnoozeRequestDialog
                isOpen={snoozeOpen}
                requestId={data.requestId}
                onClose={() => setSnoozeOpen(false)}
            />
        </div>
    );
};

SnoozeRequestControl.propTypes = {
    conversationId: PropTypes.string,
};
